import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Phone, Mail, Clock, MessageCircle, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import SEO from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Check if value means "closed" in any language
const isClosed = (value) => {
  if (!value) return false;
  const closedValues = [
    'zatvorené', 'zatvorene', 'closed', 'geschlossen', 
    'zavřeno', 'zavreno', 'закрыто', 'закрито'
  ];
  return closedValues.includes(value.toLowerCase().trim());
};

const Contact = () => {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [contactInfo, setContactInfo] = useState(null);

  useEffect(() => {
    fetchContactInfo();
  }, []);

  const fetchContactInfo = async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings/contact`);
      if (response.ok) {
        const data = await response.json();
        setContactInfo(data);
      }
    } catch (error) {
      console.error('Error fetching contact info:', error);
    }
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error(t('contact.fill_required'));
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setIsSubmitted(true);
        toast.success(t('contact.message_sent'));
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        throw new Error(data.detail || 'Failed to send message');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error(t('contact.send_error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Default values if not loaded yet
  const info = contactInfo || {
    company_name: 'Sunify s.r.o.',
    address: { street: 'Tomášikova 5', city: 'Bratislava', postal_code: '821 01', country: 'Slovenská republika' },
    phone: '+421 233 070 865',
    whatsapp: '+421 952 411 679',
    email: 'info@sunify.sk',
    working_hours: { weekdays: '8:00 - 17:00', saturday: '9:00 - 12:00', sunday: 'Zatvorené' }
  };

  const whatsappNumber = info.whatsapp?.replace(/[^0-9]/g, '') || '421952411679';

  // SEO meta data by language
  const seoData = {
    sk: {
      title: 'Kontakt',
      description: 'Kontaktujte nás pre profesionálnu montáž slnečných ochranných fólií. Bratislava. Telefón: +421 233 070 865. WhatsApp: +421 952 411 679.',
      keywords: 'kontakt Sunify, slnečné fólie Bratislava, montáž fólií kontakt'
    },
    cs: {
      title: 'Kontakt',
      description: 'Kontaktujte nás pro profesionální montáž slunečních ochranných fólií. Telefon: +421 233 070 865.',
      keywords: 'kontakt Sunify, sluneční fólie, montáž fólií kontakt'
    },
    en: {
      title: 'Contact',
      description: 'Contact us for professional window film installation. Bratislava. Phone: +421 233 070 865. WhatsApp: +421 952 411 679.',
      keywords: 'contact Sunify, window films Bratislava, film installation contact'
    },
    de: {
      title: 'Kontakt',
      description: 'Kontaktieren Sie uns für professionelle Sonnenschutzfolien-Montage. Bratislava. Telefon: +421 233 070 865.',
      keywords: 'Kontakt Sunify, Sonnenschutzfolien Bratislava, Folienmontage Kontakt'
    },
    ru: {
      title: 'Контакты',
      description: 'Свяжитесь с нами для профессионального монтажа солнцезащитных пленок. Братислава. Телефон: +421 233 070 865. WhatsApp: +421 952 411 679.',
      keywords: 'контакты Sunify, солнцезащитные пленки Братислава, монтаж пленок контакт'
    }
  };

  const currentSeo = seoData[i18n.language] || seoData.sk;

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title={currentSeo.title}
        description={currentSeo.description}
        keywords={currentSeo.keywords}
        url="/contact"
      />
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-yellow-400 text-white py-16">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold mb-4" data-testid="contact-title">
            {t('contact.title')}
          </h1>
          <p className="text-lg text-white/90">{t('contact.subtitle')}</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Contact Information */}
          <div>
            <div className="bg-white rounded-lg shadow-md p-8 mb-8">
              <h2 className="text-2xl font-bold mb-6">{t('contact.contact_info')}</h2>
              
              <div className="space-y-6">
                {/* Address */}
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                      <MapPin className="h-6 w-6 text-orange-500" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">{t('contact.address')}</h3>
                    <p className="text-gray-600">
                      {info.address?.street}<br />
                      {info.address?.postal_code} {info.address?.city}<br />
                      {info.address?.country}
                    </p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                      <Phone className="h-6 w-6 text-orange-500" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">{t('contact.phone')}</h3>
                    <a href={`tel:${info.phone?.replace(/\s/g, '')}`} className="text-orange-500 hover:text-orange-600">
                      {info.phone}
                    </a>
                  </div>
                </div>

                {/* WhatsApp */}
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <MessageCircle className="h-6 w-6 text-green-500" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">WhatsApp</h3>
                    <a href={`https://wa.me/${whatsappNumber}`} target="_blank" rel="noopener noreferrer" className="text-green-500 hover:text-green-600">
                      {info.whatsapp}
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                      <Mail className="h-6 w-6 text-orange-500" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">{t('contact.email')}</h3>
                    <a href={`mailto:${info.email}`} className="text-orange-500 hover:text-orange-600">
                      {info.email}
                    </a>
                  </div>
                </div>

                {/* Working Hours */}
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                      <Clock className="h-6 w-6 text-orange-500" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">{t('contact.working_hours')}</h3>
                    <div className="text-gray-600">
                      <p>{t('contact.monday_friday')}: {info.working_hours?.weekdays}</p>
                      <p>{t('contact.saturday')}: {isClosed(info.working_hours?.saturday) ? t('contact.closed') : info.working_hours?.saturday}</p>
                      <p>{t('contact.sunday')}: {isClosed(info.working_hours?.sunday) ? t('contact.closed') : info.working_hours?.sunday}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-lg shadow-md p-8">
              <h2 className="text-2xl font-bold mb-6">{t('contact.contact_form')}</h2>
              
              {isSubmitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="h-8 w-8 text-green-500" />
                  </div>
                  <h3 className="text-xl font-semibold text-green-600 mb-2">{t('contact.thank_you')}</h3>
                  <p className="text-gray-600 mb-4">{t('contact.we_will_contact')}</p>
                  <button
                    onClick={() => setIsSubmitted(false)}
                    className="text-orange-500 hover:text-orange-600 font-semibold"
                  >
                    {t('contact.send_another')}
                  </button>
                </div>
              ) : (
                <form className="space-y-4" onSubmit={handleSubmit}>
                  <div>
                    <label className="block text-sm font-semibold mb-2" htmlFor="name">
                      {t('contact.name')} {t('contact.required')}
                    </label>
                    <input
                      type="text"
                      id="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder={t('contact.your_name')}
                      required
                      data-testid="contact-name-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2" htmlFor="email">
                      {t('contact.email')} {t('contact.required')}
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder={t('contact.your_email')}
                      required
                      data-testid="contact-email-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2" htmlFor="phone">
                      {t('contact.phone')}
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder={t('contact.your_phone')}
                      data-testid="contact-phone-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2" htmlFor="message">
                      {t('contact.message')} {t('contact.required')}
                    </label>
                    <textarea
                      id="message"
                      rows="5"
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder={t('contact.your_message')}
                      required
                      data-testid="contact-message-input"
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-6 py-3 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    data-testid="contact-submit"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="animate-spin mr-2 h-5 w-5" />
                        {t('contact.sending')}
                      </>
                    ) : (
                      t('contact.send_message')
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* Map */}
          <div>
            <div className="bg-white rounded-lg shadow-md overflow-hidden h-full">
              <div className="h-full min-h-[600px]">
                <iframe
                  src={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2661.6582489869847!2d${info.map_coordinates?.lng || '17.1077'}!3d${info.map_coordinates?.lat || '48.1486'}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x476c8f0b0b0b0b0b%3A0x0!2s${encodeURIComponent(info.address?.street || 'Bratislava')}!5e0!3m2!1ssk!2ssk!4v1234567890`}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Mapa"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
